import { ComponentProps } from "react";
import { DefaultItems } from "@api/internal/utils/types";
import Card from "@particles/card/Card";
import { getMetaDetails } from "@particles/card/utils/helpers";
import { FeedReadingHistoryData } from "@routes/MyAccount/utils/types";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain, getUrlQuery } from "@util/helpers";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

/**
 * Generates card properties for reading history articles and myBT.
 */
export const getMyAccountReadingHistoryCardsProps = (
  articles: FeedReadingHistoryData[],
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;

  const cardProps = articles.map(
    ({ articleId, title, publishedTime, articleUrl, sections }) => {
      const mainSection = sections?.[0];
      const _meta = getMetaDetails(mainSection);

      const props: ComponentProps<typeof Card> = {
        id: articleId,
        title,
        link: articleUrl + getUrlQuery(articleUrl, linkParams),
        updatedTime: publishedTime,
        meta: _meta,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Generates card properties for  myBT.
 */
export const getMyAccountMyBTCardsProps = (
  articles: DefaultItems[],
  isPopover?: boolean,
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;

  const cardProps = articles.map(
    (
      {
        articleData: {
          id,
          title,
          urlPath,
          media,
          publishTime,
          blurb,
          sections,
        },
      },
      index
    ) => {
      const mainMedia = media?.[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;
      const imgAlt = mainMedia?.alt || mainMedia?.caption || "";

      const uniqueName = sections?.[0]?.uniqueName || " ";

      const defaultImage =
        index === 0 ? { directoryName: uniqueName } : undefined;

      const thumbnail =
        index === 0 && imgSrc && imgWidth && imgHeight
          ? {
              src: imgSrc,
              alt: imgAlt,
              width: imgWidth,
              height: imgHeight,
            }
          : undefined;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath + getUrlQuery(urlPath, linkParams),
        updatedTime: publishTime,
        thumbnail: !isPopover ? thumbnail : undefined,
        blurb: index === 0 && !isPopover ? blurb : undefined,
        defaultImage: !isPopover ? defaultImage : undefined,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/** Generate keyword url path.
 * If the keyword url path is already absolute, return it as is.
 * Otherwise, prepend the environment domain to the keyword url path.
 */
export const generateKeywordUrlPath = (keywordUrlPath: string) => {
  const envDomain = getEnvironmentDomain(getEnvironment());

  if (keywordUrlPath.includes(envDomain)) {
    return keywordUrlPath;
  }

  return `${envDomain}${keywordUrlPath}`;
};
